import { IResponseSupportBridgeClient } from './IResponseSupportBridgeClient';
import type { PendingRecoveryEntryView, RecoveryCandidateInfo, ErrorCategory } from '../../../../serviceworker/src/bridges/storage/PendingRecoveryStore';
import {
    ResponseSupportBridgeInputMessageType,
    ListAllResponsesInputMessage,
    GetResponseDataJsonInputMessage,
    ListPendingRecoveriesInputMessage,
    RetryPendingRecoveryInputMessage,
    RetryAllPendingRecoveriesInputMessage,
    DeletePendingRecoveryInputMessage,
    GetPendingRecoveryCountInputMessage,
    ResponseDataBridgeOutputMessageType,
    ListAllResponsesResultOutputMessage,
    GetResponseDataJsonResultOutputMessage,
    ListPendingRecoveriesResultOutputMessage,
    PendingRecoveryRetrySuccessOutputMessage,
    PendingRecoveryRetryFailedOutputMessage,
    PendingRecoveryDeletedOutputMessage,
    PendingRecoveryCountResultOutputMessage,
    RetryAllPendingRecoveriesCompleteOutputMessage,
} from '../../../../serviceworker/src/bridges/responseDataBridgeMessages';

const swc = ((navigator as Navigator).serviceWorker as ServiceWorkerContainer);

/**
 * PWA implementation of the Response Support Bridge Client.
 * Communicates with the ResponseSupportBridgeWorker in the service worker
 * to provide support tool and recovery functionality.
 */
export default class PWAResponseSupportBridgeClient implements IResponseSupportBridgeClient {
    // ── Existing pending promises ───────────────────────────────────────
    private pendingListAllResponsesPromise: { resolve: Function, reject: Function, timerId?: ReturnType<typeof setTimeout> } | null = null;
    private pendingGetResponseDataJsonPromises: Map<string, { resolve: Function, reject: Function, timerId?: ReturnType<typeof setTimeout> }> = new Map();

    // ── Recovery pending promises ───────────────────────────────────────
    private pendingListRecoveries: { resolve: Function, reject: Function, timerId?: ReturnType<typeof setTimeout> } | null = null;
    private pendingRetryRecovery: Map<number, { resolve: Function, reject: Function, timerId?: ReturnType<typeof setTimeout> }> = new Map();
    private pendingRetryAll: { resolve: Function, reject: Function, timerId?: ReturnType<typeof setTimeout> } | null = null;
    private pendingDeleteRecovery: Map<number, { resolve: Function, reject: Function, timerId?: ReturnType<typeof setTimeout> }> = new Map();
    private pendingRecoveryCount: { resolve: Function, reject: Function, timerId?: ReturnType<typeof setTimeout> } | null = null;

    // ── Event listener arrays (multiple subscribers supported) ──────────
    private retrySuccessListeners: Array<(id: number, targetFilename?: string, candidateInfo?: RecoveryCandidateInfo, subject?: string) => void> = [];
    private retryFailedListeners: Array<(id: number, error: string, errorCategory: ErrorCategory) => void> = [];

    constructor() {
        this.initHandlers();
    }

    public addRetrySuccessListener(fn: (id: number, targetFilename?: string, candidateInfo?: RecoveryCandidateInfo, subject?: string) => void): void {
        this.retrySuccessListeners.push(fn);
    }

    public removeRetrySuccessListener(fn: (id: number, targetFilename?: string, candidateInfo?: RecoveryCandidateInfo, subject?: string) => void): void {
        this.retrySuccessListeners = this.retrySuccessListeners.filter(l => l !== fn);
    }

    public addRetryFailedListener(fn: (id: number, error: string, errorCategory: ErrorCategory) => void): void {
        this.retryFailedListeners.push(fn);
    }

    public removeRetryFailedListener(fn: (id: number, error: string, errorCategory: ErrorCategory) => void): void {
        this.retryFailedListeners = this.retryFailedListeners.filter(l => l !== fn);
    }

    /** Clear a timeout timer when a promise settles (both resolve and reject paths). */
    private clearPendingTimer(timerId: ReturnType<typeof setTimeout> | undefined): void {
        if (timerId !== undefined) {
            clearTimeout(timerId);
        }
    }

    private initHandlers() {
        swc.addEventListener('message', (event) => {
            const message = event.data;

            if (!Object.values(ResponseDataBridgeOutputMessageType).includes(message.type)) {
                return;
            }

            switch (message.type) {
                // ── Existing support messages ────────────────────────────
                case ResponseDataBridgeOutputMessageType.listAllResponsesResult: {
                    const msg = message as ListAllResponsesResultOutputMessage;
                    if (this.pendingListAllResponsesPromise) {
                        this.clearPendingTimer(this.pendingListAllResponsesPromise.timerId);
                        this.pendingListAllResponsesPromise.resolve(msg.userUUIDs);
                        this.pendingListAllResponsesPromise = null;
                    }
                    break;
                }
                case ResponseDataBridgeOutputMessageType.getResponseDataJsonResult: {
                    const msg = message as GetResponseDataJsonResultOutputMessage;
                    const pending = this.pendingGetResponseDataJsonPromises.get(msg.userUUID);
                    if (pending) {
                        this.clearPendingTimer(pending.timerId);
                        if (msg.error) {
                            pending.reject(new Error(msg.error));
                        } else {
                            pending.resolve(msg.json);
                        }
                        this.pendingGetResponseDataJsonPromises.delete(msg.userUUID);
                    }
                    break;
                }

                // ── Recovery messages ────────────────────────────────────
                case ResponseDataBridgeOutputMessageType.listPendingRecoveriesResult: {
                    const msg = message as ListPendingRecoveriesResultOutputMessage;
                    if (this.pendingListRecoveries) {
                        this.clearPendingTimer(this.pendingListRecoveries.timerId);
                        if (msg.error) {
                            this.pendingListRecoveries.reject(new Error(msg.error));
                        } else {
                            this.pendingListRecoveries.resolve(msg.entries);
                        }
                        this.pendingListRecoveries = null;
                    }
                    break;
                }
                case ResponseDataBridgeOutputMessageType.pendingRecoveryRetrySuccess: {
                    const msg = message as PendingRecoveryRetrySuccessOutputMessage;
                    // Resolve individual retry promise if pending
                    const pending = this.pendingRetryRecovery.get(msg.id);
                    if (pending) {
                        this.clearPendingTimer(pending.timerId);
                        pending.resolve();
                        this.pendingRetryRecovery.delete(msg.id);
                    }
                    // Notify all listeners with metadata
                    for (const fn of this.retrySuccessListeners) {
                        try { fn(msg.id, msg.targetFilename, msg.candidateInfo, msg.subject); } catch (e) { console.warn('Recovery listener threw:', e); }
                    }
                    break;
                }
                case ResponseDataBridgeOutputMessageType.pendingRecoveryRetryFailed: {
                    const msg = message as PendingRecoveryRetryFailedOutputMessage;
                    const pending = this.pendingRetryRecovery.get(msg.id);
                    if (pending) {
                        this.clearPendingTimer(pending.timerId);
                        pending.reject(new Error(msg.error));
                        this.pendingRetryRecovery.delete(msg.id);
                    }
                    for (const fn of this.retryFailedListeners) {
                        try { fn(msg.id, msg.error, msg.errorCategory); } catch (e) { console.warn('Recovery listener threw:', e); }
                    }
                    break;
                }
                case ResponseDataBridgeOutputMessageType.retryAllPendingRecoveriesComplete: {
                    // Definitive completion signal from the SW — resolves the retryAll promise
                    // without needing to count individual results.
                    if (this.pendingRetryAll) {
                        this.clearPendingTimer(this.pendingRetryAll.timerId);
                        const msg = message as RetryAllPendingRecoveriesCompleteOutputMessage;
                        this.pendingRetryAll.resolve({ successCount: msg.successCount, failCount: msg.failCount });
                        this.pendingRetryAll = null;
                    }
                    break;
                }
                case ResponseDataBridgeOutputMessageType.pendingRecoveryDeleted: {
                    const msg = message as PendingRecoveryDeletedOutputMessage;
                    const pending = this.pendingDeleteRecovery.get(msg.id);
                    if (pending) {
                        this.clearPendingTimer(pending.timerId);
                        if (msg.error) {
                            pending.reject(new Error(msg.error));
                        } else {
                            pending.resolve();
                        }
                        this.pendingDeleteRecovery.delete(msg.id);
                    }
                    break;
                }
                case ResponseDataBridgeOutputMessageType.pendingRecoveryCountResult: {
                    const msg = message as PendingRecoveryCountResultOutputMessage;
                    if (this.pendingRecoveryCount) {
                        this.clearPendingTimer(this.pendingRecoveryCount.timerId);
                        if (msg.error) {
                            this.pendingRecoveryCount.reject(new Error(msg.error));
                        } else {
                            this.pendingRecoveryCount.resolve(msg.count);
                        }
                        this.pendingRecoveryCount = null;
                    }
                    break;
                }
            }
        });
    }

    // ── Existing methods ────────────────────────────────────────────────

    public listAllResponses(): Promise<string[]> {
        if (!swc.controller) {
            return Promise.reject(new Error("No service worker controller available"));
        }
        return new Promise((resolve, reject) => {
            // Reject + clear any in-flight request to prevent orphaned promises
            // and stale timeout timers that could later reject a fresh call.
            if (this.pendingListAllResponsesPromise) {
                this.clearPendingTimer(this.pendingListAllResponsesPromise.timerId);
                this.pendingListAllResponsesPromise.reject(new Error("Superseded by new listAllResponses call"));
            }
            const timerId = setTimeout(() => {
                if (this.pendingListAllResponsesPromise) {
                    this.pendingListAllResponsesPromise.reject(new Error("Timeout waiting for listAllResponses response"));
                    this.pendingListAllResponsesPromise = null;
                }
            }, 10000);
            this.pendingListAllResponsesPromise = { resolve, reject, timerId };
            swc.controller!.postMessage(new ListAllResponsesInputMessage());
        });
    }

    public getResponseDataJson(userUUID: string): Promise<string> {
        if (!swc.controller) {
            return Promise.reject(new Error("No service worker controller available"));
        }
        return new Promise((resolve, reject) => {
            // Reject + clear any in-flight request for the same userUUID.
            const existing = this.pendingGetResponseDataJsonPromises.get(userUUID);
            if (existing) {
                this.clearPendingTimer(existing.timerId);
                existing.reject(new Error("Superseded by new getResponseDataJson call"));
            }
            const timerId = setTimeout(() => {
                if (this.pendingGetResponseDataJsonPromises.has(userUUID)) {
                    this.pendingGetResponseDataJsonPromises.get(userUUID)!.reject(new Error("Timeout waiting for getResponseDataJson response"));
                    this.pendingGetResponseDataJsonPromises.delete(userUUID);
                }
            }, 30000);
            this.pendingGetResponseDataJsonPromises.set(userUUID, { resolve, reject, timerId });
            swc.controller!.postMessage(new GetResponseDataJsonInputMessage(userUUID));
        });
    }

    // ── Recovery methods ────────────────────────────────────────────────

    public listPendingRecoveries(): Promise<PendingRecoveryEntryView[]> {
        if (!swc.controller) {
            return Promise.reject(new Error("No service worker controller available"));
        }
        return new Promise((resolve, reject) => {
            // Reject any in-flight request to prevent orphaned promises
            if (this.pendingListRecoveries) {
                this.clearPendingTimer(this.pendingListRecoveries.timerId);
                this.pendingListRecoveries.reject(new Error("Superseded by new listPendingRecoveries call"));
            }
            const timerId = setTimeout(() => {
                if (this.pendingListRecoveries) {
                    this.pendingListRecoveries.reject(new Error("Timeout waiting for listPendingRecoveries"));
                    this.pendingListRecoveries = null;
                }
            }, 10000);
            this.pendingListRecoveries = { resolve, reject, timerId };
            swc.controller!.postMessage(new ListPendingRecoveriesInputMessage());
        });
    }

    public retryPendingRecovery(id: number): Promise<void> {
        if (!swc.controller) {
            return Promise.reject(new Error("No service worker controller available"));
        }
        return new Promise((resolve, reject) => {
            // Supersede any in-flight retry for the same id.
            const existing = this.pendingRetryRecovery.get(id);
            if (existing) {
                this.clearPendingTimer(existing.timerId);
                existing.reject(new Error("Superseded by new retryPendingRecovery call"));
            }
            const timerId = setTimeout(() => {
                if (this.pendingRetryRecovery.has(id)) {
                    this.pendingRetryRecovery.get(id)!.reject(new Error("Timeout waiting for retryPendingRecovery"));
                    this.pendingRetryRecovery.delete(id);
                }
            }, 120000); // 2 min — re-pack + upload can take time
            this.pendingRetryRecovery.set(id, { resolve, reject, timerId });
            swc.controller!.postMessage(new RetryPendingRecoveryInputMessage(id));
        });
    }

    /**
     * Retries all retryable entries. Resolves with { successCount, failCount }
     * when the SW sends the RetryAllPendingRecoveriesCompleteOutputMessage.
     * No client-side countdown — the SW determines when the batch is done.
     */
    public retryAllPendingRecoveries(): Promise<{ successCount: number; failCount: number }> {
        if (!swc.controller) {
            return Promise.reject(new Error("No service worker controller available"));
        }
        return new Promise((resolve, reject) => {
            if (this.pendingRetryAll) {
                this.clearPendingTimer(this.pendingRetryAll.timerId);
                this.pendingRetryAll.reject(new Error("Superseded by new retryAllPendingRecoveries call"));
            }
            const timerId = setTimeout(() => {
                if (this.pendingRetryAll) {
                    this.pendingRetryAll.reject(new Error("Timeout waiting for retryAllPendingRecoveries"));
                    this.pendingRetryAll = null;
                }
            }, 300000); // 5 min
            this.pendingRetryAll = { resolve, reject, timerId };
            swc.controller!.postMessage(new RetryAllPendingRecoveriesInputMessage());
        });
    }

    public deletePendingRecovery(id: number): Promise<void> {
        if (!swc.controller) {
            return Promise.reject(new Error("No service worker controller available"));
        }
        return new Promise((resolve, reject) => {
            // Supersede any in-flight delete for the same id.
            const existing = this.pendingDeleteRecovery.get(id);
            if (existing) {
                this.clearPendingTimer(existing.timerId);
                existing.reject(new Error("Superseded by new deletePendingRecovery call"));
            }
            const timerId = setTimeout(() => {
                if (this.pendingDeleteRecovery.has(id)) {
                    this.pendingDeleteRecovery.get(id)!.reject(new Error("Timeout waiting for deletePendingRecovery"));
                    this.pendingDeleteRecovery.delete(id);
                }
            }, 10000);
            this.pendingDeleteRecovery.set(id, { resolve, reject, timerId });
            swc.controller!.postMessage(new DeletePendingRecoveryInputMessage(id));
        });
    }

    public getPendingRecoveryCount(): Promise<number> {
        if (!swc.controller) {
            return Promise.reject(new Error("No service worker controller available"));
        }
        return new Promise((resolve, reject) => {
            if (this.pendingRecoveryCount) {
                this.clearPendingTimer(this.pendingRecoveryCount.timerId);
                this.pendingRecoveryCount.reject(new Error("Superseded by new getPendingRecoveryCount call"));
            }
            const timerId = setTimeout(() => {
                if (this.pendingRecoveryCount) {
                    this.pendingRecoveryCount.reject(new Error("Timeout waiting for getPendingRecoveryCount"));
                    this.pendingRecoveryCount = null;
                }
            }, 10000);
            this.pendingRecoveryCount = { resolve, reject, timerId };
            swc.controller!.postMessage(new GetPendingRecoveryCountInputMessage());
        });
    }
}
